import React, { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import getShipperById from '@/_service/shipper.service';
import { commonActions } from '@/_store/common/common.slice';

import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { branchService } from '@/_service/branch.service';
import { checkoutActions } from '@/_store/checkout/checkout.slice';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useActiveBrand } from '../../_hooks/useActiveBrand';
import Time from '../Time';

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

interface TimeProps {
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  showTrigger?: boolean;
}

export default function FutureTimePicker({
  open,
  onOpen,
  onClose,
  showTrigger = true,
}: TimeProps) {
  const { common, settings, session, checkout } = useSelector(
    (state: IRedux) => ({
      common: state.common,
      settings: state.settings,
      session: state.session,
      checkout: state.checkout,
    }),
    shallowEqual
  );
  const { order_type } = session;
  const { future_time } = checkout;

  const shipperId = useActiveBrand()
  const storeBranch = common?.branches?.[shipperId]

  const dispatch = useDispatch();
  const t = useTranslations();

  const { data: shipper } = useQuery({
    queryKey: ['shipper-by-id', settings?.data?.shipper_id],
    queryFn: () =>
      getShipperById(settings?.data?.shipper_id || '').then((res) => res.data),
    enabled: Boolean(settings?.data?.shipper_id),
  });

  const { data: scheduleData } = useQuery({
    queryKey: ['branch_delivery_time', storeBranch?.id],
    queryFn: () =>
      branchService
        .getWorkingHours({
          object_id: storeBranch?.id,
          object_type: 'branch_delivery_time',
        })
        .then((res) => res?.data),
    enabled: Boolean(storeBranch?.id),
  });

  useEffect(() => {
    if (shipper) {
      dispatch(commonActions.futureTime(shipper?.future_order_time));
    }
  }, [shipper]);

  const interval = useMemo(() => {
    if (
      storeBranch?.future_self_pickup_order_time ||
      storeBranch?.future_delivery_order_time
    ) {
      if (
        (order_type?.type === 'self-pickup' || order_type?.type === 'hall') &&
        +storeBranch?.future_self_pickup_order_time !== 0
      ) {
        return parseInt(storeBranch?.future_self_pickup_order_time);
      }
      if (
        order_type?.type === 'hall' &&
        +storeBranch?.future_self_pickup_order_time !== 0
      ) {
        return parseInt(storeBranch?.future_self_pickup_order_time);
      }
      if (
        order_type?.type === 'delivery' &&
        +storeBranch?.future_delivery_order_time !== 0
      ) {
        return parseInt(storeBranch?.future_delivery_order_time);
      }
    }

    return common?.future_order_time ? parseInt(common?.future_order_time) : 0;
  }, [storeBranch, order_type?.type, common?.future_order_time]);

  const day = useMemo(() => {
    if (!future_time) return null; // Handle missing data

    if (dayjs(future_time).isSame(dayjs(), 'day')) {
      return t('today');
    } else if (dayjs(future_time).isSame(dayjs().add(1, 'day'), 'day')) {
      return t('tomorrow');
    } else {
      return dayjs(future_time).format('DD MMMM');
    }
  }, [future_time]);

  const formattedTime = useMemo(() => {
    if (!future_time) {
      if (settings?.data?.data?.is_pre_order_time === false) {
        return t('when_ready');
      }
      return `${t('when_ready')} (${t('in_x_min', {
        x:
          (order_type?.type === 'delivery' && storeBranch?.delivery_time) ||
          interval ||
          60,
      })})`;
    }

    const futureTime = dayjs(future_time);
    const startTime = futureTime.format('HH:mm');

    return `${day}, ${startTime}`;
  }, [future_time, day, t, interval, settings?.data?.data?.is_pre_order_time]);

  const onConfirm = (time: string) => {
    if (time) {
      dispatch(checkoutActions.changeFutureTime(time));
    } else {
      dispatch(checkoutActions.changeFutureTime(null));
    }
  };

  return (
    <Time
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      interval={interval}
      onConfirm={onConfirm}
      showTrigger={showTrigger}
      scheduleData={scheduleData}
      formattedTime={formattedTime}
    />
  );
}
